import React from 'react';
import { Grid, Typography, TypographyProps, TypographyTypeMap } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

export interface IconLabelProps<
  LabelTypographyComponent extends React.ElementType = TypographyTypeMap['defaultComponent'],
  DescriptionTypographyComponent extends React.ElementType = TypographyTypeMap['defaultComponent'],
  TextTypographyComponent extends React.ElementType = TypographyTypeMap['defaultComponent'],
> {
  label?: React.ReactNode;
  description?: React.ReactNode;
  text?: React.ReactNode;
  children: React.ReactNode;
  optional?: React.ReactNode;
  error?: boolean;
  dense?: boolean;
  labelUppercase?: boolean;
  labelNormal?: boolean;
  labelTypographyProps?: TypographyProps<
    LabelTypographyComponent,
    {
      component?: LabelTypographyComponent;
    }
  >;
  descriptionTypographyProps?: TypographyProps<
    DescriptionTypographyComponent,
    {
      component?: TextTypographyComponent;
    }
  >;
  textTypographyProps?: TypographyProps<
    TextTypographyComponent,
    {
      component?: TextTypographyComponent;
    }
  >;
  onLabelClick?: () => void;
  descriptionMedium?: boolean;
  mobile?: boolean;
  noMargin?: boolean;
  descriptionInherit?: boolean;
}

const useStyles = makeStyles()((theme: any) => ({
  label: {
    wordBreak: 'break-word',
    '&.clickable': {
      cursor: 'pointer',
    },
  },
  bold: {
    fontWeight: theme.typography.fontWeightMediumBold,
  },
  medium: {
    fontWeight: theme.typography.fontWeightMedium,
    margin: 0,
  },
  description: {
    wordBreak: 'break-word',
  },
  unDense: {
    marginTop: '4px',
  },
  uppercase: {
    textTransform: 'uppercase',
  },
  error: {
    color: theme.palette.secondary.main,
  },
  labelMobile: {
    '&&&': {
      fontSize: 15,
      fontWeight: 700,
      lineHeight: '24px',
    },
  },
  descriptionMobile: {
    '&&&': {
      fontSize: 11,
      fontWeight: 500,
      lineHeight: '13px',
    },
  },
  noMargin: {
    marginTop: 0,
    marginBottom: 0,
  },
  descriptionInherit: {
    lineHeight: '12px',
    color: '#383C44',
    fontSize: 13,
    fontWeight: 500,
  },
}));

const IconLabel = <
  LabelTypographyComponent extends React.ElementType = TypographyTypeMap['defaultComponent'],
  DescriptionTypographyComponent extends React.ElementType = TypographyTypeMap['defaultComponent'],
  TextTypographyComponent extends React.ElementType = TypographyTypeMap['defaultComponent'],
>({
  label,
  description,
  text,
  children,
  optional,
  error,
  dense,
  labelUppercase,
  labelTypographyProps,
  descriptionTypographyProps,
  textTypographyProps,
  onLabelClick,
  labelNormal,
  descriptionMedium,
  mobile,
  noMargin,
  descriptionInherit,
}: IconLabelProps<LabelTypographyComponent, DescriptionTypographyComponent, TextTypographyComponent>): JSX.Element => {
  const { classes, cx } = useStyles();
  return (
    <Grid container spacing={2} wrap="nowrap" alignItems="center">
      <Grid item>
        <Grid container alignItems="center">
          {children}
        </Grid>
      </Grid>
      <Grid item container zeroMinWidth alignContent="center">
        {label && (
          <Grid item xs={12}>
            <Typography
              onClick={onLabelClick}
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              className={cx(classes.label, {
                clickable: onLabelClick,
                [classes.uppercase]: labelUppercase,
                [classes.bold]: !labelNormal,
                [classes.labelMobile]: mobile,
              })}
              variant="h6"
              {...labelTypographyProps}
            >
              {label}
            </Typography>
          </Grid>
        )}
        {description && (
          <Grid item xs={12} className={cx(classes.description, { [classes.error]: error, [classes.unDense]: !dense })}>
            <Typography color="textSecondary" variant="body2" {...descriptionTypographyProps}>
              <p
                className={cx({
                  [classes.noMargin]: noMargin,
                  [classes.medium]: descriptionMedium,
                  [classes.descriptionMobile]: mobile,
                  [classes.descriptionInherit]: descriptionInherit,
                })}
              >
                {description}
              </p>
            </Typography>
          </Grid>
        )}
        {text && (
          <Grid item xs={12} className={cx(classes.description, { [classes.unDense]: !dense })}>
            <Typography variant="body2" {...textTypographyProps}>
              {text}
            </Typography>
          </Grid>
        )}
        {optional}
      </Grid>
    </Grid>
  );
};

export default IconLabel;
